import { router } from 'expo-router';
import { capitalize } from 'lodash';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import BrandedButton from '@/components/buttons/BrandedButton';
import FormWrapper from '@/components/forms/FormWrapper';
import FileInputField from '@/components/inputs/FileInputField';
import OnboardingSection from '@/components/npo/onboarding/OnboardingSection';
import SaveAndContinueLaterButton from '@/components/npo/onboarding/SaveAndContinueLaterButton';
import Text from '@/components/Text';
import { PLData, pLResolver } from '@/lib/validations/NpoOnboardingSchema';
import { i18n } from '@/utils/i18n';

interface NpoPLFormProps {
  initialData:
    | {
        pL: string | undefined;
        balanceSheet: string | undefined;
      }
    | undefined;
  isLoading: boolean;
  showBackButton?: boolean;
  showSaveAndContinueButton?: boolean;
  submitLabel?: string;
  fileProgress?: number;
  indexUploading?: number;
  onSubmitTapped: (data: PLData) => void;
}

const NpoPLForm = ({
  initialData,
  isLoading,
  showBackButton = true,
  showSaveAndContinueButton = true,
  submitLabel = i18n.t('mix.next'),
  fileProgress,
  indexUploading,
  onSubmitTapped
}: NpoPLFormProps) => {
  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid, submitCount, errors }
  } = useForm<PLData>({
    defaultValues: initialData,
    mode: 'onChange',
    resolver: pLResolver as any
  });

  const onSubmit: SubmitHandler<PLData> = async (data) => {
    onSubmitTapped(data);
  };

  return (
    <FormWrapper scrollEnable={false}>
      <View style={styles.container}>
        <View style={styles.fields}>
          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.pl.pl_section')}
            description={i18n.t('sign_in.nonProfit.pl.pl_section_description')}>
            <FileInputField
              control={control}
              name="pL"
              progress={
                (getValues('pL') && indexUploading === 0) || (getValues('balanceSheet') && indexUploading === 1)
                  ? fileProgress
                  : undefined
              }
              acceptedTypes={['application/pdf', 'image/jpeg', 'image/png']}
            />
          </OnboardingSection>

          <OnboardingSection
            label={i18n.t('sign_in.nonProfit.pl.balance_sheet_section')}
            description={i18n.t('sign_in.nonProfit.pl.balance_sheet_section_description')}>
            <FileInputField
              control={control}
              name="balanceSheet"
              progress={
                (getValues('balanceSheet') && indexUploading === 0) || (getValues('pL') && indexUploading === 1)
                  ? fileProgress
                  : undefined
              }
              acceptedTypes={['application/pdf', 'image/jpeg', 'image/png']}
            />
          </OnboardingSection>
        </View>

        <View style={styles.buttonsContainer}>
          {errors.root && <Text style={styles.errorText}>{errors?.root?.message}</Text>}

          <View style={styles.nextBackContainer}>
            {showBackButton && router.canGoBack() && (
              <BrandedButton disabled={isLoading} style={styles.flexOne} onPress={router.back}>
                {capitalize(i18n.t('mix.back'))}
              </BrandedButton>
            )}
            <BrandedButton
              style={styles.flexOne}
              loading={isLoading}
              disabled={!isValid && submitCount > 0}
              onPress={handleSubmit(onSubmit)}>
              {submitLabel}
            </BrandedButton>
          </View>
          {showSaveAndContinueButton && <SaveAndContinueLaterButton disabled={isLoading} />}
        </View>
      </View>
    </FormWrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between'
  },

  fields: {
    flexDirection: 'column',
    gap: 25
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  },

  flexOne: {
    flex: 1
  },

  nextBackContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 10
  },

  buttonsContainer: {
    flexDirection: 'column',
    gap: 10
  }
});

export default NpoPLForm;
